import * as Sentry from '@sentry/react';
import { startTransition, StrictMode } from 'react';
import { is_sms_link_path } from '~/lib/sms-link-scrub';
import { scrub_replay_recording, scrub_sentry } from '~/lib/sentry-scrub';
import { REPLAY_PRIVACY } from '~/lib/sentry-replay-privacy';

// A text's sign-in link (/s/<token>) is a credential in its URL. A page load
// that BEGINS on it never starts Replay: the replay would record that URL as
// its first page (and in its replay_event's visited-URL list), and a replay
// whose opening snapshot is dropped is unplayable anyway. Such a session is
// not replayed at all, its later pages included; every other session is
// recorded as before. Everything Sentry sends is scrubbed of URL credentials
// (?token=, ?code=, /s/<token>, …) and Authorization/Cookie headers
// (lib/sentry-scrub.ts), and Replay masks all text and inputs and blocks
// media and embedded payment fields (lib/sentry-replay-privacy.ts).
const replay = !is_sms_link_path(window.location.pathname);

Sentry.init({
  dsn: 'https://25f220f6208545b33bf9cdda07974040@o4507914525802496.ingest.us.sentry.io/4507914531504128',
  sendDefaultPii: false,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.browserSessionIntegration(),
    ...(replay
      ? [
          Sentry.replayIntegration({
            ...REPLAY_PRIVACY,
            beforeAddRecordingEvent: (event) => scrub_replay_recording(event),
          }),
        ]
      : []),
  ],
  beforeSend: (event) => scrub_sentry(event),
  beforeSendTransaction: (event) => scrub_sentry(event),
  beforeBreadcrumb: (breadcrumb) => scrub_sentry(breadcrumb),
  replaysSessionSampleRate: 1.0,
  replaysOnErrorSampleRate: 1.0,
  tracesSampleRate: 0.0,
  attachStacktrace: true,
  enabled: !window.location.href.includes('localhost'),
});

// Every event the SDK prepares, the replay's own replay_event (its visited
// URLs, which beforeSend never sees) included: that one as strictly as the
// recording itself.
Sentry.addEventProcessor((event) =>
  (event.type as string | undefined) === 'replay_event' ? scrub_replay_recording(event) : scrub_sentry(event),
);

import { RemixBrowser } from '@remix-run/react';
import { hydrateRoot } from 'react-dom/client';

startTransition(() => {
  hydrateRoot(
    document,
    <StrictMode>
      <RemixBrowser />
    </StrictMode>,
  );
});
